import { DEFAULT_PV_LOSSES } from "./pvLosses";
import { initialDemandShape } from "./demandShape";
import { DEFAULT_MERCHANT_REVENUE_PROJECTION } from "./merchantRevenueProjection";
import { DEFAULT_TOLLING_FLOOR_UPSIDE } from "./tollingFloorUpside";
import { DEFAULT_TOLLING_FIXED_CAPACITY } from "./tollingFixedCapacity";
import { DEFAULT_HYBRID_PARTIAL_TOLLING } from "./hybridPartialTolling";
import { DEFAULT_PROJECT_DEBT } from "./projectDebt";
import { DEFAULT_BALANCING_V1_CONFIG } from "./balancingV1";
import { DEFAULT_CAPACITY_PAYMENTS_CONFIG } from "./capacityPayments";
import { DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG } from "./guaranteesOfOrigin";
import { DEFAULT_PPA_CONFIG } from "./ppa";
import { DEFAULT_REGULATORY_FRAMEWORK } from "./regulatoryFramework";
import { DEFAULT_CONNECTION_CURTAILMENT } from "./connectionCurtailment";
import { DEFAULT_BTM } from "./btm";
import { DEFAULT_DEMAND_PROJECTION } from "./demandProjection";
import { DEFAULT_WIND } from "../../utils/windConfig";
import { DEFAULT_COMMERCIAL_OFFER } from "./commercialOffer";

export const DEFAULT_CONFIG = {
  projectName: "Untitled Project",
  // Language of the proposal PDF and the teasers. The templates carry both
  // languages side by side and the renderer shows one of them; a PDF is
  // rendered without JavaScript, so the choice has to be made before the run
  // rather than at download time. "en" keeps every report exactly as it was
  // before the bilingual work.
  reportLanguage: "en",
  timeZone: "Europe/Athens",
  biddingZone: "GR",
  address: "Athens, Greece",
  roof_area: 3500,
  // loc_info.tilt/azimuth are the TERRAIN slope and the direction it faces, not
  // the array's. The default was 25 deg falling south - the same way the panels
  // face - which steps every row down behind the one in front and switches row
  // shading off completely: 0.99% of annual beam lost becomes 0.00% at 4.8 m
  // pitch in northern Greece, and a tight pitch then looks free. Flat is the
  // honest default: it is the common case, and it is the one that does not
  // quietly rewrite the physics for anyone who never opens the field.
  loc_info: { lat: 37.98, lon: 23.73, tilt: 0, azimuth: 180 },
  useCase: "Commercial",
  surfaceType: "grass",
  systemType: "Hybrid", // "pv_only" | "bess_only" | "hybrid",
  settlementType: "Arbitrage",
  // Flexible connection & curtailment (spec Feature 6). Every value here is
  // neutral: no curtailment, no securities, no COD delay, so a project that
  // never touches the section reproduces its previous numbers exactly.
  connectionCurtailment: DEFAULT_CONNECTION_CURTAILMENT,
  // Which markets the project intends to bid into. DAM is always included by the
  // dispatch engine today; IDA1-3 are captured here ahead of backend support and
  // do not yet change the optimization run (see Optimize.jsx "Market Participation").
  marketParticipation: {
    dam: true,
    ida1: false,
    ida2: false,
    ida3: false,
  },
  ppaStartHour: "09:00",
  ppaEndHour: "21:00",
  ppaType: "Fixed",
  ppaFixedPrice: 0.0,
  ppaFirmBlockLimit: 50.0,
  ppaTodBasePrice: 0.0, 


  tollingFixedFeePerMWYear: 0.0, //
  tollingContractYears: 10,
  tollingAvailabilityPct: 95,
  tollingPenaltyAnnual: 0.0, //
  tollingRevenueFloor: 0,
  tollingUpsideSharePct: 20,
  hybridContractedSharePct: 70, //
  hybridTollFeePerMWYear: 0, //
  hybridMerchantSharePct: 30, //
  tollingSettlementFrequency: "Monthly",
  tollingEscalationPct: 0,
  tollingCapacityBasis: "InstalledMW",
  tollingFixedCapacity: DEFAULT_TOLLING_FIXED_CAPACITY,
  tollingFloorUpside: DEFAULT_TOLLING_FLOOR_UPSIDE,
  hybridPartialTolling: DEFAULT_HYBRID_PARTIAL_TOLLING,
  ppa: DEFAULT_PPA_CONFIG,
  // Behind-the-Meter (self-consumption & peak shaving). Only read when
  // settlementType === "BTM"; see config/btm.js and the backend twin
  // BESS_feasibility/Configuration/btm.py.
  btm: DEFAULT_BTM,
  arbitrageMerchant: {
    includedRevenueStreams: {
      dam: true,
      intraday: false,
      ida1: false,
      ida2: false,
      ida3: false,
      balancing: false,
      ancillary: false,
      capacity: false,
      go: false,
      other: false,
    },
    balancingV1: DEFAULT_BALANCING_V1_CONFIG,
    capacityPayments: DEFAULT_CAPACITY_PAYMENTS_CONFIG,
    guaranteesOfOrigin: DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG,
    // Greek RES + storage regulatory framework (Category 11A / 11B).
    // mode "none" keeps today's behaviour; see config/regulatoryFramework.js.
    regulatoryFramework: DEFAULT_REGULATORY_FRAMEWORK,
  },
  marketStack: {
    includedMarkets: ["DAM"],
  },
  useMerchantOptimization: true,
  merchantRevenueProjection: DEFAULT_MERCHANT_REVENUE_PROJECTION,


  dispatch_resolution: "15",
  grid: {
    connectionLevel: "MV",     // LV | MV | HV
    // Line-to-line connection voltage. Typical per level: LV 400 V,
    // MV 20 kV (Greece), HV 150 kV. A physical input: drives the POI
    // cable-run losses (I²R ∝ 1/V²) and the LV/MV/HV consistency checks.
    voltageV: 20000,
    // Opt-in: bind exportLimitKw / importLimitKw as 24/7 hard POI constraints
    // in the dispatch for EVERY revenue model (historically they bound only
    // under 11A/11B and BtM). Off by default: existing dispatches unchanged.
    enforceLimits: false,
    exportLimitKw: 1500,        // kW (grid injection limit)
    importLimitKw: 1500,        // kW (grid draw limit)
  },
  allowGridInjection: true,
  simultaneousInjectDraw: false,
  pvInstalledCap: { kw: 150 },
  // Auto-design tuning. Stored with the project rather than held in component
  // state, because these change the design that gets saved: a layout nobody can
  // reproduce is not much of a deliverable, and previously the dialog reset to
  // these defaults on every reload with no record of what produced the design
  // actually in the project.
  //   wCost          0 = pick the most efficient design, 1 = pick the cheapest.
  //                  Scored as (1 - wCost) * efficiency + wCost * (1 - cost).
  //   dcacMin/Max    allowed DC/AC band (total DC / inverter AC).
  //   oversizeRatio  cap on actual DC against target: DC <= ratio * target.
  //   minCellC/maxCellC  cell temperatures the string voltage window is checked
  //                     at: Voc at the cold one against the DC ceiling, Vmp at
  //                     the hot one against the inverter's MPPT floor.
  // targetCapacityKw is the DC size auto-design searches for. Blank means
  // "use the project's installed capacity", which is what every project
  // saved before this field existed was last designed against.
  autoDesign: { wCost: 0.8, dcacMin: 1.0, dcacMax: 1.2, oversizeRatio: 1.05, minCellC: -10, maxCellC: 70, targetCapacityKw: "", inverterPhases: "both", modulesOwnedOnly: false, invertersOwnedOnly: false },
  // "new" (default): PV CAPEX included. "existing": sunk asset (retrofit) —
  // backend excludes PV/inverter hardware + installation from CAPEX, keeps O&M.
  pvAssetStatus: "new",
  annualDegFactor: 1.5,
  mountType: "fixed",
  axisOrientation: "North-South",
  maxAngle: 75,
  backTrack: false,
  // mountingConfig drives the SAPM cell-temperature parameter set. The default
  // reproduces the value that used to be hardcoded in the engine, so existing
  // projects keep their current results.
  // transpositionModel / aoiModel mirror pv_core.model_defaults; the backend
  // normalises anything unknown or absent back to these, so older saved
  // projects keep running on the recommended models.
  panelOrientation: {
    tilt: 30,
    azim: 180,
    // "horizontal" (pvlib / PVsyst plane tilt) or "ground": the racking follows
    // the terrain, so the typed tilt is between modules and rails and the
    // engine is handed the plane composed with the site's ground slope.
    tiltReference: "horizontal",
    mountingConfig: "open_rack_glass_polymer",
    transpositionModel: "perez",
    spectralModel: "first_solar",
    aoiModel: "auto",
    thermalModel: "sapm",
    acModel: "auto",
    // How neighbouring rows blocking the sky is accounted for. "free_standing"
    // applies the analytic masking-angle correction and keeps Perez; the
    // alternative resolves full view factors but cannot. Matches the backend
    // default in pv_core, so a project without the key behaves the same.
    arrayGeometry: "free_standing",
    // Row-on-row shadow at low sun. "bypass" models diodes switching whole
    // substrings out, which is the physical behaviour and far larger than the
    // shaded area alone. The effect fraction blends toward a plain area loss.
    nearShadingModel: "bypass",
    nearShadingElectricalEffect: 1,
    // Portrait puts the bypass substrings in vertical strips, so a shadow on
    // the bottom edge takes the whole module; landscape loses a third as much.
    // Portrait matches how the layout builder sizes rows.
    moduleOrientation: "portrait",
    // Datasheet facts about the selected module, set on the Equipment tab.
    // Half-cut wires two halves in parallel, so a bottom-edge shadow costs half
    // a module rather than all of it. Diodes matter only in landscape.
    moduleHalfCut: true,
    moduleBypassDiodes: 6,
  },
  read_module: "JKM590N-72HL4-BDV",
  read_inverter: "SUN2000-150K-MG0",
  systemDesign: {},
  pvLosses: DEFAULT_PV_LOSSES,
  hasTransformer: false,
  useRecommendedTransformer: true,
  // POI chain in the dispatch (dev spec section 4 / A3). Every new field has
  // a default that reproduces today's behaviour: chainLossMode "off" builds
  // the optimization model exactly as before; the extra parameters are only
  // read once a chain mode is selected.
  transformerDets: {
    rated: "",
    noLoadLoss: "",
    loadLoss: "",
    // "none" | "shared_poi" | "per_asset" — resolved to shared_poi by the
    // backend when hasTransformer is on and nothing is chosen.
    topology: "shared_poi",
    // Per-asset only: false = the battery has NO dedicated step-up unit (an
    // explicit, declared choice - e.g. MV PCS with integrated transformer).
    includeBessUnit: true,
    // "off" (today's behaviour) | "opex_only" (losses costed after the solve,
    // zero dispatch risk) | "in_dispatch" (transformer inside the MILP).
    chainLossMode: "off",
    powerFactor: 0.95,
    energisedHoursYear: 8760,
    // Fill P0/Pk from the EU Ecodesign 548/2014 typical values when the user
    // has no datasheet (fields stay editable; flagged as "default").
    useEcodesignDefaults: true,
    // chainSegments and negativePriceHandling used to live here. Both were
    // numerical-method settings with one right answer for every project, so the
    // engine now fixes them (6 chords, loss always pinned to the curve) and the
    // report discloses what it used. Older saved projects may still carry these
    // keys; the engine logs that they no longer apply and ignores them.
    // Fold the AC run's I²R losses into the same loss chain (spec 4.8). Needs
    // the cable geometry below; with it off (default) nothing changes.
    includeCableLosses: false,
    // Typical MV feeder values so a new project starts from something
    // sensible instead of empty required fields; review per site. Empty
    // voltage -> the run operates at the grid connection voltage.
    cable: { lengthM: 150, sectionMm2: 240, material: "Cu", parallelRuns: 1, acVoltageV: "" },
    // per_asset topology: the battery-block transformer (required then).
    battery: { rated: "", noLoadLoss: "", loadLoss: "" },
    // per_asset topology: the wind-block transformer (applied exogenously on
    // the wind generation series; active in dispatch once wind enters the
    // feasibility optimization).
    wind: { rated: "", noLoadLoss: "", loadLoss: "" },
    // per_asset topology: OPTIONAL common MV/HV unit above the asset blocks,
    // gated by the explicit include toggle below.
    includeHv: false,
    hv: { rated: "", noLoadLoss: "", loadLoss: "" },
  },
  // Charge taper (dev spec C1): SoC-dependent charge power limit for the CV
  // phase. Off by default — existing projects reproduce their results exactly.
  chargeTaper: { enabled: false, kneeSocPct: 85 },
  wind: DEFAULT_WIND,
  bess: {
    selected: [
    ],
    activeName: "", // battery currently shown in the UI
    // Electrical topology used by dispatch. Old projects without this key
    // remain AC-coupled; DC coupling must be chosen explicitly.
    topology: "ac-coupled",
    dcDcEfficiencyPct: "",
    dcPortChargeKw: "",
    dcPortDischargeKw: "",
    pcs: { model: "", powerKw: "" },
  },
  batteryDetails: { iniSoC: 50, startDaySoC: 50, dailyCycles: 1, degradationCostMWh: "",  degradationModel: "linear_per_cycle", lifetimeThroughputMWh: "",fixedLifetimeYears: 15, minIdleMins: 0,     },
  // Battery performance panel (dev spec section 10; items B1/B2, A1/A2, A5,
  // Γ2/Γ3). New projects get the CORRECT stream-split degradation by default
  // ("annual_pct" @ 2%/yr on the battery stream); old saved projects without
  // this key keep the legacy single-factor behaviour exactly.
  batteryPerformance: {
    // "legacy" | "annual_pct" | "warranty_curve" | "pysam_derived"
    degradationMode: "annual_pct",
    bessAnnualDegPct: 2.0,
    // OEM warranty table, free text "year:pct, ..." e.g. "1:97, 5:91, 10:85"
    retentionCurve: "",
    // "off" | "affine" — affine PCS loss (k0 + k1·P) in the optimization (A2).
    // NEW projects default to "affine": every BESS has a PCS and the default
    // parameters scale with the rated power. Saved projects keep their value.
    pcsLossMode: "affine",
    // Where the two loss terms come from: "typical" (the two fields below) or
    // "curve" (fitted from the datasheet table). Stored explicitly so the
    // datasheet option can be selected before any point has been entered.
    pcsLossSource: "typical",
    pcsNoLoadKw: "",        // k0; empty -> 0.005 × P_rated
    pcsMarginalLoss: "",    // k1; empty -> 0.012
    // Datasheet efficiency table "load %:efficiency %, ..."; when set it fits
    // k0/k1 and takes precedence over the two fields above.
    pcsEfficiencyCurve: "",
    // If given, REPLACES the whole conversion chain (cell + PCS + trf).
    systemRtePct: "",
    // Auxiliary loads (HVAC/BMS) — explicit kW, never an efficiency factor.
    // Off for new projects; the two figures below are only offered once the
    // operator opts in, and each of them stays individually optional.
    auxLoadsEnabled: false,
    auxIdleKw: "",
    auxKwPerMwThroughput: "",
    // Availability (Γ3): applied as a power derate. Standard practice
    // defaults for new projects (~72 h planned, ~2% forced).
    plannedOutageHoursYear: 72,
    forcedOutageRatePct: 2.0,
    // Annual warranty limits (Γ2): ENFORCED in dispatch when set.
    maxAnnualCycles: "",
    maxAnnualThroughputMWh: "",
    // A4: ambient temperature also affects round-trip efficiency (off = power only).
    tempEfficiencyEnabled: false,
    // A4: optional datasheet table "temp °C : efficiency %" that replaces the
    // standard curve in the dispatch (empty = standard curve).
    tempEfficiencyCurve: "",
    // A6: linear self-discharge of stored energy (typical Li-ion 1–3 %/month; 0 = off).
    selfDischargePctPerMonth: 2,
    // Γ4: hold SoC head/foot-room for the committed aFRR band (off = power derate only).
    afrrEnergyCompliance: false,
    afrrActivationDurationH: 1,
    // B3: augmentation model — "manual" (E2 fields) | "trigger" (year derived
    // from the retention curve) | "fom_pct" (continuous % of battery CAPEX).
    augmentationMode: "manual",
    augmentationTriggerPct: 80,
    augmentationFomPctYear: 2.5,
    // Maintenance reserve: pre-fund the one-off top-up with equal annual
    // set-asides in the years before it, instead of one cash-flow spike.
    augmentationReserveEnabled: false,
    // B4: re-dispatch at degraded capacity. The mode is stored explicitly;
    // multiYearRedispatchMode "representative" resolves the year list from the
    // project horizon, "custom" keeps whatever the user picked.
    multiYearRedispatchMode: "off",
    multiYearAnchors: "",
    // B4b: a funded top-up RESTORES capacity in one year, so the capacity
    // path has a step in it. Interpolating revenue linearly between an
    // anchor before the step and one after it climbs across the
    // discontinuity and overstates the years just before the top-up. When
    // on, the engine adds the top-up year and the year before it to the
    // anchor list itself, so a measured point sits on each side of the step.
    //
    // ON by default. Measured on the reference 11A project across four runs
    // with otherwise identical inputs, every anchor list that interpolated
    // across the step overstated NPV - by up to 3.80%. It costs up to two
    // extra full-year optimizations, which is the only reason it remains a
    // switch at all. The engine reads an ABSENT value as on too, so this
    // default and BESSForm's `raw === undefined ? true : ...` must stay in
    // step with normalize_battery_performance_config.
    anchorAugmentationTrough: true,
  },
  batCount: { dummy: 0 },
  allowGridCharging: true,
  simultaneousGridPv: false,
  simultaneousChDis: false,
  clientInfo: {
    // Demo / illustrative mode (item D): the backend neutralises the client
    // identity, suppresses the confidentiality stamp and watermarks every
    // page "ILLUSTRATIVE". Off by default.
    demoMode: false,
    // Optional pre-export naming gate: when stated and different from clName,
    // the backend REFUSES to export (raises), instead of shipping a
    // confidential report addressed to the wrong party.
    intendedRecipient: "",
    clName: "ABC Solar Development Ltd",
    contact_person: "",
    contact_role: "",
    client_email: "name@company.com",
    client_phone: "",
    client_address: "",
    project_title: "",
    study_objective: "",
  },
  // Optional named potential offtakers / counterparties (entered in
  // Feasibility > General). Rendered in the report's Potential Offtakers
  // section via read_config.potentialOfftakers.
  potentialOfftakers: [],
  // Optional permitting / consents status (entered in Feasibility > General).
  // Each blank status renders as "To be confirmed" in the report's Permitting &
  // Consents section, so an untouched project honestly flags them as pending DD.
  permitting: {
    landControl: "",
    gridConnection: "",
    environmental: "",
    construction: "",
    note: "",
  },
  engInfo: {
    eng_name: "Engineer Name",
    eng_title: "Energy Engineer",
    eng_registration: "",
    eng_company: "MetEn Solutions",
    eng_mail: "engineer@email.com",
    eng_phone: "",
  },
  // Saved per asset, used only in the personalised offer PDF, not the run fingerprint.
  commercialOffer: { ...DEFAULT_COMMERCIAL_OFFER,
    // Unset means use the asset-type default; a user's explicit false stays false.
    pricing: { ...DEFAULT_COMMERCIAL_OFFER.pricing, include_pv_only: undefined },
  },
  metaInfo: {
    prep_date: new Date().toISOString().slice(0, 10),
    study_version: "Rev 0",
    study_approvedBy: "Reviewer",
    study_approvalNotes: "",
    document_id: "",
  },
  feasibility: {
    simulationYear: new Date().getFullYear() - 1,
    // New projects start in the current calendar year. Legacy saved projects
    // without this field retain their original reference year as their start.
    operatingStartYear: new Date().getFullYear(),
    // Derived automatically from simulationYear (366 in leap years); kept for
    // config schema backward compatibility only — not user-editable.
    simulationHorizonDays: String(
      ((y) => ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 366 : 365))(
        new Date().getFullYear() - 1
      )
    ),
    settlementPeriod: "20",
    technical: {
      is_billing: false,
      is_single_zonal: true,
      peakWindow: {},
      inptTariffs: {},
      carryover_enabled: false,
      // Multi-year demand projection + load uncertainty. Inert by default: with
      // no growth, no steps and no uncertainty the engine dispatches once, as
      // before. See config/demandProjection.js and the backend twin
      // BESS_feasibility/Configuration/demand_projection.py.
      demandProjection: DEFAULT_DEMAND_PROJECTION,
      demand: {
        enabled: false,
        inputType: "Monthly Consumption (kWh)",
        monthly: {},
        weights: {},
        shape: initialDemandShape("Commercial"),
        // Optional metered profile: { values: [...], resolution, source }.
        // When present and complete it REPLACES the modelled profile.
        loadProfile: null,
        // Persisted defaults, deliberately matching what the schedule selects
        // DISPLAY. They used to be empty strings while the selects fell back to
        // "09:00" etc. for display only, so a user who never touched a dropdown
        // saved a site that is open zero hours a week — and the engine then
        // generated a year of zero load without complaining.
        schedule: {
          weekdays: { startHour: "09:00", endHour: "17:00" },
          saturdays: { startHour: "10:00", endHour: "14:00" },
          sundays: { startHour: "", endHour: "" },
        },
      },
      tariffs: {
        // New studies use explicit pre-VAT bill components. Existing projects
        // keep their saved mode (or the legacy fallback when no mode was saved),
        // so an old effective rate is never reinterpreted as supply-only.
        rateMode: "itemized",
        // Independent final monthly amounts for the effective-price mode.
        legacyFixed: { fixedMonthlyEur: "", otherFixedMonthlyEur: "" },
        itemized: { pricingMode: "single", monthly: {}, regulatedEurKwh: "", ykoEurKwh: "0", fixedMonthlyEur: "", otherFixedMonthlyEur: "", vatRatePct: "6", vatRecoverablePct: "0" },
        // MUST be persisted, not left to a display fallback. The backend reads
        // an ABSENT billingScheme as "Single zonal" (is_single_zonal_scheme
        // returns True for anything without "bi" in it, including ""), while
        // the select used to display "Bi-zonal" as its fallback. A project
        // whose user never touched the control therefore showed one scheme on
        // screen and was dispatched as the other - the same defect the workday
        // schedule had.
        billingScheme: "Single zonal",
        // Dynamic (hourly) tariff, BTM only: used when billingScheme is
        // "Dynamic". Blank prices are 0; negative day-ahead hours are billed at
        // zero plus the fee unless passNegativePrices is on.
        dynamic: { supplierFeeEurKwh: "", regulatedEurKwh: "", passNegativePrices: false },
        offPeak: {},
        midPeak: {},
        onPeak: {},
        shoulder: {},
      },
    },
    economics: {
      // Equipment cost overrides. Empty string means "use the catalog", which
      // is the default and the normal case: the CAPEX hardware term is catalog
      // unit price x installed count, and that is the number a reviewer can
      // check. An override exists for the case the catalog cannot express -- a
      // negotiated price, a quoted package, hardware bought before the study --
      // and it replaces the catalog value EVERYWHERE, including the basis for
      // the percentage O&M and insurance rates. One number, one meaning.
      //
      // The *Name fields record which catalog model each override was typed
      // against, so swapping the module/inverter/battery can drop an override
      // that no longer describes anything rather than carrying a price for
      // equipment the study no longer contains.
      equipmentCostOverride: {
        modules: "",
        inverters: "",
        battery: "",
        moduleName: "",
        inverterName: "",
        batteryName: "",
      },
      investmentCosts: {
        moduleEurPerWdc: "",
        inverterEurPerWdc: "",
        batteryEurPerWhdc: "",
        gridInterconnectionEurPerKwp: "",
        transformerEurPerKva: "",
        designEngineeringEurPerKwp: "",
        laborPermitsPctSubtotal: "5",
        moduleRecurringPctYear: "",
        inverterRecurringPctYear: "",
        batteryRecurringPctYear: "",
        landLeasingEurYear: "",
        mountingStructuresEurPerKwp: "",
        commissioningTestsEurPerKwp: "",
        cablingConnectionsEurPerKwp: "",
        electricalProtectionEurSite: "",
        monitoringMeteringEurSite: "",
        contingencyPctSubtotal: "5",
      },
      financialParams: {
        inflationRatePctYear: "2.0",
        nominalDiscountRatePctYear: "7.0",
        insuranceRatePctYear: "0.5",
        capexGrantPct: "0",
        generationIncentiveEurPerKwh: "0",

        // P90/P99 downside-exceedance uncertainty budget (percent). Combined in
        // quadrature in the report; the price/revenue component is scaled by the
        // project's merchant exposure. Conservative defaults; leave as-is unless a
        // specific resource/market study justifies different values.
        p90ResourceUncertaintyPct: "4",
        p90PriceUncertaintyPct: "15",
        // Behind-the-meter only: 1-sigma on the RETAIL tariff level, applied to
        // the tariff-valued share of the saving (the exported share takes the
        // market price term above). Matches the +/-10% tariff downside case.
        btmTariffUncertaintyPct: "10",
        // Monte-Carlo revenue simulation (item F1 V2-lite). ON by default:
        // the exhibit is additive-only and changes no base number (seeded,
        // deterministic). Switch off per project to hide it. rho is a
        // labelled house assumption (1 = one systematic shock, 0 =
        // independent years).
        stochasticSimulationEnabled: true,
        stochasticYearCorrelation: "0.7",
        stochasticPaths: "2000",
        // M&A / asking-price view (item F8 V1). 0 = no exhibit, nothing
        // changes. Share-deal convention stated in the report.
        askingPriceEur: "0",
        p90ModelUncertaintyPct: "5",

        // Revenue-quality inputs (balancing-responsible-party view). Neutral
        // defaults reproduce previous numbers exactly: 100% availability,
        // 0 EUR/MWh imbalance. Applied to the merchant settlement value
        // (merchant / 11A / tolling merchant basis); availability also
        // derates the PPA delivered volume and the battery-driven share of
        // BtM savings.
        bessAvailabilityPct: "100",
        // Blank = "same as the BESS figure" (previous behaviour, bit-identical).
        // Set it where the settlement separates the PV and battery legs (11A).
        pvAvailabilityPct: "",
        imbalanceCostEurPerMwh: "0",

        // Greek corporate tax engine (item F3). OFF by default: the legacy
        // flat cash tax runs and existing numbers are unchanged. When ON:
        // straight-line depreciation (law 4172/2013 art. 24) and 5-year loss
        // carry-forward (art. 27); no interest shield (conservative). All
        // values are labelled assumptions in the report.
        taxEngineEnabled: false,
        pvDepreciationRatePct: "10",
        bessDepreciationRatePct: "10",
        lossCarryForwardYears: "5",

        debtPercent: "70",
        tenorYears: "10",
        annualInterestRatePct: "5.0",
        moratoriumYears: "0",
        debtClosingCostsEur: "0",
        upFrontFeePct: "1.0",
        realDiscountRate: "",
        incomeTax: "",
        salvageValue: "",
        landLease_cost: "",
        loanStructure: "sculpted",
        targetDSCR: "1.30",
        bulletMaturityYear: "",

        // Battery augmentation (dev spec E2): both fields have worked in the
        // backend for a while (read_config -> bess_sizing books the cost in
        // the chosen year) but had no UI. Year 0 / cost 0 = no augmentation.
        augmentationYear: "0",
        augmentationCostEur: "0",
      },
      projectDebt: DEFAULT_PROJECT_DEBT,
    },
  },
  configFile: "api-config",
};
